import { ClientLoadModel, DriverPriceType, Toll } from '@numeo/types-core'
import axios from 'axios'
import { CircularProgress, Box, Stack, Button, Skeleton } from '@mui/joy'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { useAuth } from 'hooks/auth'
import { MapCard } from './MapCard'
import { ProfitCard } from './ProfitCard'
import { CostsCard } from './CostsCard'
import { TollDetailsCard } from './TollDetailsCard'
import { WeatherCard } from './WeatherCard'
import { useLocation } from 'react-router-dom'
import AssistantDirectionIcon from '@mui/icons-material/AssistantDirection'
// Type for route response
interface RouteResponse {
    routeId: string
    polyline: string
    duration: number
    length: number
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tolls: any[]
}

/**
 * LoadRouteAndToll component displays the route and toll information for a load.
 * This includes:
 * - Route map
 * - Duration
 * - Distance
 * - Tolls
 * - Toll details
 * - Google Maps sharing functionality
 */

interface Props {
    load: ClientLoadModel
    direction?: 'row' | 'column'
    truckId: string
    variant?: 'compact' | 'default'
}

export const LoadRouteAndToll: React.FC<Props> = ({ load, direction = 'row', truckId, variant = 'compact' }) => {
    const { application } = useAuth()
    const truck = application?.temp.spotFinder?.trucks.find((t) => t._id === truckId)
    const driver = application?.users?.find((user) => user.id === String(truck?.drivers?.[0]))
    const driverCostType = driver?.compensation?.type || DriverPriceType.PER_MILE
    const driverCost = driver?.compensation?.value || 0.6
    const fuelCost = truck?.pricePerGallon || 3.5
    const [isLoading, setIsLoading] = useState(false)
    const [polyline, setPolyline] = useState<string | undefined>(load?.polyline)
    const [duration, setDuration] = useState<number | undefined>(load?.duration || undefined)
    const [processedTolls, setProcessedTolls] = useState<Toll[] | undefined>(load?.tolls)
    const [weatherWarnings, setWeatherWarnings] = useState<Array<{ condition: string; location: string; temp?: number; icon: string }>>([])
    const [hasMapData, setHasMapData] = useState<boolean>(false) // Track if we have map data

    const location = useLocation()

    // Helper function to extract coordinates from GeoJSON format
    const getCoordinates = useCallback((location: { coordinates?: number[]; longitude?: number; latitude?: number }) => {
        if (location?.coordinates && Array.isArray(location.coordinates) && location.coordinates.length >= 2) {
            return {
                longitude: location.coordinates[0],
                latitude: location.coordinates[1],
            }
        }
        // Fallback to direct properties if they exist
        return {
            longitude: location?.longitude,
            latitude: location?.latitude,
        }
    }, [])

    // Calculate initial MPG based on load weight
    const calculateInitialMpg = (weight?: number): string => {
        if (!weight) return '7' // Default MPG if no weight
        if (weight < 10000) return '7.5'
        if (weight < 30000) return '6.5'
        return '5.5' // For very heavy loads (30,000+ lbs)
    }

    // Base states
    const [tripLength, setTripLength] = useState<string>(load?.tripLength?.toString() || '0')
    const [deadheadMiles, setDeadheadMiles] = useState<number>(0)
    const [includeDeadhead, setIncludeDeadhead] = useState<boolean>(false)
    const [baseRouteMiles, setBaseRouteMiles] = useState<number>(load?.tripLength || 0)

    // Rate and cost states
    const [ratePerMile, setRatePerMile] = useState<string>(
        load.rateInfo?.rateUsd && load.tripLength ? String((load.rateInfo?.rateUsd / load.tripLength).toFixed(2)) : '2.5'
    )
    const [totalRate, setTotalRate] = useState<string>(String(load.rateInfo?.rateUsd || 2.5 * (load.tripLength || 0)))
    const [mpg, setMpg] = useState<string>(calculateInitialMpg(load.shipmentDetails.maximumWeightPounds))
    const [driverCostPerMile, setDriverCostPerMile] = useState<string>((driverCostType === DriverPriceType.PER_MILE ? Number(driverCost) : 0.55).toFixed(2))
    const [pricePerGallon, setPricePerGallon] = useState<string>((Number(fuelCost) || 3.5).toFixed(2))

    const getCardWidths = () => {
        if (variant === 'compact') {
            return {
                map: direction === 'row' ? '20%' : '25%',
                profit: direction === 'row' ? '15%' : '25%',
                costs: direction === 'row' ? '20%' : '25%',
                toll: direction === 'row' ? '15%' : '25%',
                weather: direction === 'row' ? '15%' : '25%',
            }
        } else {
            return {
                map: direction === 'row' ? '44%' : '35%',
                profit: direction === 'row' ? '44%' : '30%',
                costs: direction === 'row' ? '44%' : '30%',
                toll: direction === 'row' ? '44%' : '25%',
                weather: direction === 'row' ? '44%' : '25%',
            }
        }
    }

    const cardWidths = getCardWidths()

    // Track if total rate was manually edited
    const [isRateManuallyEdited, setIsRateManuallyEdited] = useState(false)

    // Calculate deadhead miles from load data
    const calculateDeadheadMiles = useCallback(() => {
        const originMiles = load?.originDeadheadMiles?.miles || 0
        const destMiles = load?.destinationDeadheadMiles?.miles || 0
        return originMiles + destMiles
    }, [load.originDeadheadMiles, load.destinationDeadheadMiles])

    // Get current displayed miles
    const getTotalMiles = useCallback(() => {
        return parseFloat(tripLength) || 0
    }, [tripLength])

    // Process toll data from API response
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const processTollsFromResponse = useCallback((responseTolls: any[]) => {
        if (!responseTolls || !Array.isArray(responseTolls)) return []

        return responseTolls.map((toll) => ({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            total: toll.fares.reduce((sum: number, fare: any) => sum + (fare.price?.value || 0), 0),
            details: [
                {
                    countryCode: toll.countryCode,
                    tollSystemRef: toll.tollSystemRef,
                    tollSystem: toll.tollSystem,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    fares: toll.fares.map((fare: any) => ({
                        id: fare.id,
                        name: fare.name,
                        price: fare.price?.value || 0,
                        reason: fare.reason,
                        paymentMethods: fare.paymentMethods,
                    })),
                    tollCollectionLocations: toll.tollCollectionLocations,
                },
            ],
        }))
    }, [])

    // Calculate total toll cost
    const calculateTotalToll = useCallback((tolls?: Toll[]) => {
        if (!tolls || tolls.length === 0) return 0
        return tolls.reduce((sum, toll) => sum + (toll?.total || 0), 0)
    }, [])

    // Calculate all costs based on current values
    const costs = useMemo(() => {
        // Use the current trip length for all calculations
        const totalMiles = parseFloat(tripLength) || 0
        const totalRateValue = parseFloat(totalRate) || 0
        const pricePerGallonValue = parseFloat(pricePerGallon) || 3.5
        const mpgValue = parseFloat(mpg) || 7

        // Calculate driver cost based on type (FIXED or PERCENTAGE)
        let driverCostTotal = 0
        if (driverCostType === DriverPriceType.PERCENT) {
            // Calculate as percentage of total rate
            const driverCostPercentage = Number(driverCost) / 100
            driverCostTotal = totalRateValue * driverCostPercentage
        } else {
            // Fixed cost per mile
            const driverCostValue = parseFloat(driverCostPerMile) || 0.55
            driverCostTotal = totalMiles * driverCostValue
        }

        const fuelCost = totalMiles * (pricePerGallonValue / mpgValue)

        // Use processedTolls from API response if available, otherwise fall back to load.tolls
        const tollCost = calculateTotalToll(processedTolls || load?.tolls) || 0

        // Total cost is the sum of all costs
        const totalCost = driverCostTotal + fuelCost + tollCost

        return {
            driver: driverCostTotal,
            fuel: fuelCost,
            toll: tollCost,
            total: totalCost,
            deadhead: 0, // No longer needed as we're including deadhead in the main calculations
        }
    }, [tripLength, driverCostType, driverCost, driverCostPerMile, pricePerGallon, mpg, processedTolls, load?.tolls, calculateTotalToll, totalRate])

    // Calculate profit based on rate and costs
    const profit = useMemo(() => {
        const totalRateValue = parseFloat(totalRate) || 0

        // Profit is total rate minus all costs
        return totalRateValue - costs.total
    }, [totalRate, costs.total])

    // Calculate deadhead miles when load changes
    useEffect(() => {
        const miles = calculateDeadheadMiles()
        setDeadheadMiles(miles)

        // Set initial data but don't show map immediately
        if (load?.polyline) {
            setPolyline(load.polyline)
            // Don't set hasMapData to true here - wait for user to click Generate button
        }
        if (load?.tripLength) {
            setBaseRouteMiles(load.tripLength)
        }
        if (load?.tolls) {
            setProcessedTolls(load.tolls)
        }
    }, [load.polyline, load.tripLength, load.tolls, load.originDeadheadMiles, load.destinationDeadheadMiles, calculateDeadheadMiles])

    // Initialize values on first mount
    useEffect(() => {
        // Initialize total rate
        if (load?.tripLength) {
            const rpmValue = parseFloat(ratePerMile) || 0
            setTotalRate((rpmValue * load.tripLength).toFixed(0))
        }
        // eslint-disable-next-line
    }, [])

    // Handle deadhead toggle
    useEffect(() => {
        // Update trip length based on deadhead toggle
        if (includeDeadhead) {
            // Include deadhead miles in the trip length
            setTripLength((baseRouteMiles + deadheadMiles).toFixed(0))

            // Recalculate rate per mile based on the new trip length, keeping total rate constant
            if (totalRate !== '' && !isNaN(parseFloat(totalRate))) {
                const totalRateValue = parseFloat(totalRate)
                const totalMiles = baseRouteMiles + deadheadMiles
                if (totalMiles > 0) {
                    setRatePerMile((totalRateValue / totalMiles).toFixed(2))
                }
            }
        } else {
            // Use only base route miles
            setTripLength(baseRouteMiles.toString())

            // Recalculate rate per mile based on base route miles
            if (totalRate !== '' && !isNaN(parseFloat(totalRate))) {
                const totalRateValue = parseFloat(totalRate)
                if (baseRouteMiles > 0) {
                    setRatePerMile((totalRateValue / baseRouteMiles).toFixed(2))
                }
            }
        }
    }, [includeDeadhead, deadheadMiles, baseRouteMiles, totalRate, isRateManuallyEdited])

    // Utility functions
    const getPolylineForLoad = async () => {
        setIsLoading(true)
        try {
            // Extract coordinates properly from GeoJSON format
            const originCoords = getCoordinates(load.origin)
            const destinationCoords = getCoordinates(load.destination)

            // Validate that we have valid coordinates
            if (!originCoords.latitude || !originCoords.longitude || !destinationCoords.latitude || !destinationCoords.longitude) {
                console.error('Invalid coordinates:', { originCoords, destinationCoords })
                return
            }

            const type = `${location.pathname}` === '/dispatcher/communications' ? 'extension' : 'default'

            const response = await axios.get<{ success: boolean; data: RouteResponse }>(`/trucks/loads/route`, {
                params: {
                    loadId: load._id,
                    originLat: originCoords.latitude,
                    originLng: originCoords.longitude,
                    destinationLat: destinationCoords.latitude,
                    destinationLng: destinationCoords.longitude,
                    truckId,
                    projectName: application?.projectName,
                    type,
                },
            })

            // Use the response data directly instead of waiting for websocket
            if (response.data?.success && response.data?.data) {
                const routeData = response.data.data

                // Handle polyline - could be a URL or direct polyline string
                if (routeData.polyline) {
                    setPolyline(routeData.polyline)
                    setHasMapData(true)
                }

                // Update route data if available in response
                if (routeData.length) {
                    // Update rate calculations if needed
                    if (!isRateManuallyEdited && ratePerMile !== '') {
                        const rpmValue = parseFloat(ratePerMile)
                        setTotalRate((rpmValue * (load.tripLength || 0)).toFixed(0))
                    }
                }

                // Process tolls data if available in the response
                if (routeData.tolls && Array.isArray(routeData.tolls)) {
                    // This is a local state update only - doesn't affect the load object
                    // The backend already updates the load in the database
                    const tolls = processTollsFromResponse(routeData.tolls)
                    setProcessedTolls(tolls)
                }

                // Set duration if available
                if (routeData.duration) {
                    setDuration(routeData.duration)
                }
            }
        } catch (error) {
            console.error('Error fetching polyline:', error)
        } finally {
            setIsLoading(false)
        }
    }

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0,
        }).format(amount)
    }

    // Open Google Maps with the route
    const openInGoogleMaps = useCallback(() => {
        // Extract coordinates properly from GeoJSON format
        const originCoords = getCoordinates(load.origin)
        const destinationCoords = getCoordinates(load.destination)

        if (!originCoords.latitude || !originCoords.longitude || !destinationCoords.latitude || !destinationCoords.longitude) {
            console.error('Invalid coordinates for Google Maps:', { originCoords, destinationCoords })
            return
        }

        const originCoordsStr = `${originCoords.latitude},${originCoords.longitude}`
        const destCoordsStr = `${destinationCoords.latitude},${destinationCoords.longitude}`
        const url = `https://www.google.com/maps/dir/?api=1&origin=${originCoordsStr}&destination=${destCoordsStr}&travelmode=driving`
        window.open(url, '_blank')
    }, [load?.origin, load?.destination, getCoordinates])

    const tooltipMsg = `Based on load weight: ${load.shipmentDetails.maximumWeightPounds ? load.shipmentDetails.maximumWeightPounds.toLocaleString() : 'N/A'} lbs`

    // Map skeleton component
    const MapSkeleton = ({ height = 200, variant = 'compact' }: { height?: number; variant?: 'compact' | 'default' }) => {
        const skeletonHeight = variant === 'compact' ? height - 120 : height - 100

        return (
            <Box
                sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 'sm',
                    height: height,
                    position: 'relative',
                    backgroundColor: 'background.level1',
                }}
            >
                {/* Generate Polyline Button */}
                <Box sx={{ position: 'absolute', top: 3, left: 3 }}>
                    <Button
                        size="sm"
                        variant="solid"
                        color="primary"
                        onClick={getPolylineForLoad}
                        disabled={isLoading}
                        startDecorator={isLoading ? <CircularProgress size="sm" /> : <AssistantDirectionIcon />}
                        sx={{
                            fontSize: '13px',
                        }}
                    >
                        {isLoading ? 'Generating...' : 'Show map and toll info'}
                    </Button>
                </Box>

                {!isLoading && (
                    <>
                        <Skeleton
                            variant="text"
                            width="60%"
                            height={24}
                            sx={{
                                mb: variant === 'compact' ? 2 : 1,
                                mt: 6,
                                zIndex: 0,
                            }}
                        />
                        <Skeleton
                            variant="rectangular"
                            width="100%"
                            height={skeletonHeight}
                            sx={{
                                mb: 1,
                                zIndex: 0,
                                borderRadius: 'sm',
                            }}
                        />
                    </>
                )}
            </Box>
        )
    }

    return (
        <Box sx={{ position: 'relative', overflow: 'auto' }}>
            {direction === 'row' ? (
                <Stack direction="row" width={'100%'} spacing={variant === 'compact' ? 1 : 2} px={1} flexWrap={'wrap'} alignItems="stretch">
                    {/* Map card or skeleton */}
                    <Box width={cardWidths.map} height="100%">
                        {hasMapData && polyline ? (
                            <MapCard polyline={polyline} openInGoogleMaps={openInGoogleMaps} onWeatherUpdate={setWeatherWarnings} />
                        ) : (
                            <MapSkeleton height={variant === 'compact' ? 235 : 200} variant={variant} />
                        )}
                    </Box>

                    {/* Profit calculator card */}
                    <Box width={cardWidths.profit} height="100%">
                        <ProfitCard
                            ratePerMile={ratePerMile}
                            setRatePerMile={setRatePerMile}
                            setIsRateManuallyEdited={setIsRateManuallyEdited}
                            getTotalMiles={getTotalMiles}
                            setTotalRate={setTotalRate}
                            mpg={mpg}
                            setMpg={setMpg}
                            totalRate={totalRate}
                            includeDeadhead={includeDeadhead}
                            setIncludeDeadhead={setIncludeDeadhead}
                            tooltipMsg={tooltipMsg}
                            formatCurrency={formatCurrency}
                            profit={profit}
                            deadheadMiles={deadheadMiles}
                        />
                    </Box>

                    {/* Cost breakdown card */}
                    <Box width={cardWidths.costs} height="100%">
                        <CostsCard
                            totalRate={totalRate}
                            driverCostPerMile={driverCostPerMile}
                            setDriverCostPerMile={setDriverCostPerMile}
                            pricePerGallon={pricePerGallon}
                            setPricePerGallon={setPricePerGallon}
                            tripLength={tripLength}
                            setTripLength={setTripLength}
                            setIsRateManuallyEdited={setIsRateManuallyEdited}
                            ratePerMile={ratePerMile}
                            setTotalRate={setTotalRate}
                            costs={costs}
                            duration={duration}
                            formatCurrency={formatCurrency}
                            driverCostType={driverCostType}
                            driverCostValue={Number(driverCost)}
                        />
                    </Box>

                    {/* Toll details card */}
                    <Box width={cardWidths.toll} height="100%">
                        <TollDetailsCard load={load} tolls={processedTolls} />
                    </Box>

                    {/* Weather card */}
                    {weatherWarnings.length > 0 && (
                        <Box width={cardWidths.weather} height="100%">
                            <WeatherCard weatherWarnings={weatherWarnings} />
                        </Box>
                    )}
                </Stack>
            ) : (
                <Stack direction="column" width={'100%'} spacing={variant === 'compact' ? 1 : 2} px={1}>
                    {variant === 'compact' ? (
                        <>
                            <Stack direction="row" width={'100%'} spacing={1} justifyContent="space-between" alignItems="stretch">
                                {/* Map card or skeleton */}
                                <Box width={cardWidths.map} height="100%">
                                    {hasMapData && polyline ? (
                                        <MapCard polyline={polyline} openInGoogleMaps={openInGoogleMaps} onWeatherUpdate={setWeatherWarnings} />
                                    ) : (
                                        <MapSkeleton height={variant === 'compact' ? 235 : 200} variant={variant} />
                                    )}
                                </Box>

                                {/* Profit calculator card */}
                                <Box width={cardWidths.profit} height="100%">
                                    <ProfitCard
                                        ratePerMile={ratePerMile}
                                        setRatePerMile={setRatePerMile}
                                        setIsRateManuallyEdited={setIsRateManuallyEdited}
                                        getTotalMiles={getTotalMiles}
                                        setTotalRate={setTotalRate}
                                        mpg={mpg}
                                        setMpg={setMpg}
                                        totalRate={totalRate}
                                        includeDeadhead={includeDeadhead}
                                        setIncludeDeadhead={setIncludeDeadhead}
                                        tooltipMsg={tooltipMsg}
                                        formatCurrency={formatCurrency}
                                        profit={profit}
                                        deadheadMiles={deadheadMiles}
                                    />
                                </Box>

                                {/* Cost breakdown card */}
                                <Box width={cardWidths.costs} height="100%">
                                    <CostsCard
                                        totalRate={totalRate}
                                        driverCostPerMile={driverCostPerMile}
                                        setDriverCostPerMile={setDriverCostPerMile}
                                        pricePerGallon={pricePerGallon}
                                        setPricePerGallon={setPricePerGallon}
                                        tripLength={tripLength}
                                        setTripLength={setTripLength}
                                        setIsRateManuallyEdited={setIsRateManuallyEdited}
                                        ratePerMile={ratePerMile}
                                        setTotalRate={setTotalRate}
                                        costs={costs}
                                        duration={duration}
                                        formatCurrency={formatCurrency}
                                        driverCostType={driverCostType}
                                        driverCostValue={Number(driverCost)}
                                    />
                                </Box>

                                {/* Toll details card */}
                                <Box width={cardWidths.toll} height="100%">
                                    <TollDetailsCard load={load} tolls={processedTolls} />
                                </Box>
                            </Stack>

                            {/* Weather card - separate row if exists */}
                            {weatherWarnings.length > 0 && (
                                <Box width={cardWidths.weather} sx={{ mt: 1 }}>
                                    <WeatherCard weatherWarnings={weatherWarnings} />
                                </Box>
                            )}
                        </>
                    ) : (
                        // Default variant - 2x2 grid
                        <>
                            {/* First row - Map and Profit */}
                            <Stack direction="row" width={'100%'} spacing={2} justifyContent="space-between" alignItems="stretch">
                                <Box width="50%" height="100%">
                                    {hasMapData && polyline ? (
                                        <MapCard polyline={polyline} openInGoogleMaps={openInGoogleMaps} onWeatherUpdate={setWeatherWarnings} />
                                    ) : (
                                        <MapSkeleton height={300} />
                                    )}
                                </Box>

                                <Box width="50%" height="100%">
                                    <ProfitCard
                                        ratePerMile={ratePerMile}
                                        setRatePerMile={setRatePerMile}
                                        setIsRateManuallyEdited={setIsRateManuallyEdited}
                                        getTotalMiles={getTotalMiles}
                                        setTotalRate={setTotalRate}
                                        mpg={mpg}
                                        setMpg={setMpg}
                                        totalRate={totalRate}
                                        includeDeadhead={includeDeadhead}
                                        setIncludeDeadhead={setIncludeDeadhead}
                                        tooltipMsg={tooltipMsg}
                                        formatCurrency={formatCurrency}
                                        profit={profit}
                                        deadheadMiles={deadheadMiles}
                                    />
                                </Box>
                            </Stack>

                            {/* Second row - Costs and Toll */}
                            <Stack direction="row" width={'100%'} spacing={2} justifyContent="space-between" alignItems="stretch">
                                <Box width="50%" height="100%">
                                    <CostsCard
                                        totalRate={totalRate}
                                        driverCostPerMile={driverCostPerMile}
                                        setDriverCostPerMile={setDriverCostPerMile}
                                        pricePerGallon={pricePerGallon}
                                        setPricePerGallon={setPricePerGallon}
                                        tripLength={tripLength}
                                        setTripLength={setTripLength}
                                        setIsRateManuallyEdited={setIsRateManuallyEdited}
                                        ratePerMile={ratePerMile}
                                        setTotalRate={setTotalRate}
                                        costs={costs}
                                        duration={duration}
                                        formatCurrency={formatCurrency}
                                        driverCostType={driverCostType}
                                        driverCostValue={Number(driverCost)}
                                    />
                                </Box>

                                <Box width="50%" height="100%">
                                    <TollDetailsCard load={load} tolls={processedTolls} />
                                </Box>
                            </Stack>

                            {/* Weather card - separate row if exists */}
                            {weatherWarnings.length > 0 && (
                                <Box width="50%" sx={{ mt: 2 }}>
                                    <WeatherCard weatherWarnings={weatherWarnings} />
                                </Box>
                            )}
                        </>
                    )}
                </Stack>
            )}
        </Box>
    )
}
